import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal } from "antd";
import moment from "moment";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import {
  APIS,
  DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT,
} from "../../../app/utils/constants/apiConstant";
import {
  hasPendingMonthlyAccessRequest,
  readMonthlyAccessStatusCache,
  writeMonthlyAccessStatusCache,
} from "../../../app/utils/common/monthlyAccessStatusCache";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "./MonthlyAccessRequestModal.scss";

const ACCESS_REQUEST_REASON_MIN_LENGTH = 4;
const ACCESS_REASON_MAX_LENGTH = 2000;
export const ACCESS_SCOPE_PREVIOUS = "previous";
export const ACCESS_SCOPE_CURRENT = "current";

const MonthlyAccessRequestModal = ({
  open,
  onClose,
  onGoToPreviousMonth,
  onStatusChange,
  scope = ACCESS_SCOPE_PREVIOUS,
  /** Parent already loaded access-status — prefer this so open is instant. */
  initialStatus = null,
}) => {
  const [status, setStatus] = useState(
    () => initialStatus || readMonthlyAccessStatusCache()
  );
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reason, setReason] = useState("");
  // Keep latest callback in a ref so loadStatus stays stable and opening the
  // modal does not re-trigger access-status in a parent re-render loop.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const isCurrentScope = scope === ACCESS_SCOPE_CURRENT;

  const loadStatus = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.TASK_MONTHLY_ACCESS_STATUS,
        method: "GET",
      });
      const data = response?.data?.data || null;
      setStatus(data);
      writeMonthlyAccessStatusCache(data);
      if (typeof onStatusChangeRef.current === "function") {
        onStatusChangeRef.current(data);
      }
    } catch (_error) {
      const cached = readMonthlyAccessStatusCache();
      if (cached) setStatus(cached);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const initialStatusRef = useRef(initialStatus);
  initialStatusRef.current = initialStatus;

  useEffect(() => {
    if (!open) return;
    setReason("");
    const fromParent = initialStatusRef.current || readMonthlyAccessStatusCache();
    if (fromParent) {
      setStatus(fromParent);
      if (!initialStatusRef.current || hasPendingMonthlyAccessRequest(fromParent)) {
        loadStatus({ quiet: true });
      }
      return;
    }
    loadStatus();
  }, [open, loadStatus]);

  useEffect(() => {
    if (open && initialStatus) setStatus(initialStatus);
  }, [open, initialStatus]);

  const hasPendingPreviousMonth = Boolean(status?.hasPendingUpdates);
  const accessRequestStatus = String(status?.requestStatus || "").toLowerCase();
  const hasPendingAccessRequest = accessRequestStatus === "pending";
  const canEditPreviousMonth = Boolean(
    status?.canEditPreviousMonth ||
      (accessRequestStatus === "approved" && hasPendingPreviousMonth)
  );
  const previousMonthLabel =
    status?.previousMonth ||
    moment().subtract(1, "month").format("MMMM YYYY");

  const hasCurrentMonthMissed = Boolean(status?.hasCurrentMonthMissedUpdates);
  const currentMonthRequestStatus = String(
    status?.currentMonthRequestStatus || ""
  ).toLowerCase();
  const hasPendingCurrentMonthRequest = currentMonthRequestStatus === "pending";
  const canEditCurrentMonthMissed = Boolean(
    status?.canEditCurrentMonthMissed ||
      (currentMonthRequestStatus === "approved" && hasCurrentMonthMissed)
  );
  const currentMonthLabel =
    status?.currentMonth || moment().format("MMMM YYYY");

  const hasPendingTargetRequest = isCurrentScope
    ? hasPendingCurrentMonthRequest
    : hasPendingAccessRequest;
  // Approval only covers the requested dates; newly missed dates need a fresh request.
  const needsTargetRequest = Boolean(
    isCurrentScope
      ? status?.needsCurrentMonthRequest
      : status?.needsPreviousMonthRequest
  );
  const canEditTarget =
    !needsTargetRequest &&
    !hasPendingTargetRequest &&
    (isCurrentScope ? canEditCurrentMonthMissed : canEditPreviousMonth);
  const targetMonthLabel = isCurrentScope
    ? currentMonthLabel
    : previousMonthLabel;

  const handleClose = () => {
    setReason("");
    if (typeof onClose === "function") onClose();
  };

  const requestAccess = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < ACCESS_REQUEST_REASON_MIN_LENGTH) {
      showErrorToast(
        `Please explain why you missed your updates (at least ${ACCESS_REQUEST_REASON_MIN_LENGTH} characters).`
      );
      return;
    }
    setRequesting(true);
    try {
      const response = await protectedApi({
        url: APIS.TASK_MONTHLY_ACCESS_REQUEST,
        method: "POST",
        data: {
          requestReason: trimmedReason,
          scope: isCurrentScope ? ACCESS_SCOPE_CURRENT : ACCESS_SCOPE_PREVIOUS,
        },
      });
      showSuccessToast(
        response?.data?.message || "Approval request sent to HR/Admin."
      );
      window.dispatchEvent(new Event(DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT));
      setReason("");
      await loadStatus();
      handleClose();
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        "Unable to send access request.";
      showErrorToast(
        typeof message === "string" ? message : "Unable to send access request."
      );
    } finally {
      setRequesting(false);
    }
  };

  const footer = canEditTarget
    ? [
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
        !isCurrentScope ? (
          <Button
            key="go-prev"
            type="primary"
            onClick={() => {
              if (typeof onGoToPreviousMonth === "function") {
                onGoToPreviousMonth();
              } else {
                handleClose();
              }
            }}
          >
            Go to {targetMonthLabel}
          </Button>
        ) : (
          <Button key="done" type="primary" onClick={handleClose}>
            OK
          </Button>
        ),
      ]
    : hasPendingTargetRequest
      ? [
          <Button key="close" type="primary" onClick={handleClose}>
            Close
          </Button>,
        ]
      : [
          <Button key="close" onClick={handleClose}>
            Close
          </Button>,
          <Button
            key="request-access"
            type="primary"
            loading={requesting || loading}
            disabled={reason.trim().length < ACCESS_REQUEST_REASON_MIN_LENGTH}
            onClick={requestAccess}
          >
            Request Access
          </Button>,
        ];

  return (
    <Modal
      open={open}
      title={
        isCurrentScope
          ? "Missed Current Month Updates"
          : "Pending Daily Work Updates"
      }
      onCancel={handleClose}
      closable
      maskClosable
      footer={footer}
      className="monthly-access-request-modal"
    >
      {canEditTarget ? (
        isCurrentScope ? (
          <>
            <p>
              Your request has been approved. You can now complete your missed{" "}
              <strong>{targetMonthLabel}</strong> daily work updates.
            </p>
            <p>
              Use the calendar and click each <strong>Missed</strong> day to fill
              updates.
            </p>
          </>
        ) : (
          <>
            <p>
              Your request has been approved. You can now complete your{" "}
              <strong>{targetMonthLabel}</strong> daily work updates.
            </p>
            <p>
              Use the calendar: open <strong>{targetMonthLabel}</strong> and click
              each <strong>Missed</strong> day to fill updates. Current month stays
              locked until all previous-month pending days are complete.
            </p>
          </>
        )
      ) : hasPendingTargetRequest ? (
        isCurrentScope ? (
          <>
            <p>
              You have missed daily work updates in{" "}
              <strong>{targetMonthLabel}</strong> that are past the normal
              entry window.
            </p>
            <p>
              Your access request is already pending HR/Admin review. You will be
              able to complete those days once it is approved.
            </p>
          </>
        ) : (
          <>
            <p>You have pending daily work updates from the previous month.</p>
            <p>
              Your access request for <strong>{targetMonthLabel}</strong> is
              already pending HR/Admin review. You will be able to complete missed
              days once it is approved.
            </p>
          </>
        )
      ) : isCurrentScope ? (
        <>
          <p>
            You have missed daily work updates in{" "}
            <strong>{targetMonthLabel}</strong> that are past the next-working-day
            entry window.
          </p>
          <p>
            Request approval from HR/Admin to fill those missed days mid-month.
          </p>
          <label
            className="monthly-access-request-modal__reason-label"
            htmlFor="monthly-access-request-reason"
          >
            Why did you miss these updates?
          </label>
          <Input.TextArea
            id="monthly-access-request-reason"
            rows={4}
            maxLength={ACCESS_REASON_MAX_LENGTH}
            showCount
            placeholder="Briefly explain why you could not submit daily work updates on the missed dates."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </>
      ) : (
        <>
          <p>You have pending daily work updates from the previous month.</p>
          <p>
            Please request approval from HR/Admin to complete your pending updates
            before accessing the current month work updates.
          </p>
          <label
            className="monthly-access-request-modal__reason-label"
            htmlFor="monthly-access-request-reason"
          >
            Why did you miss these updates?
          </label>
          <Input.TextArea
            id="monthly-access-request-reason"
            rows={4}
            maxLength={ACCESS_REASON_MAX_LENGTH}
            showCount
            placeholder="Briefly explain why you could not submit daily work updates on the pending dates."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </>
      )}
    </Modal>
  );
};

export default MonthlyAccessRequestModal;
